/**
 * Локальная база данных Kairo (IndexedDB через Dexie).
 *
 * Приложение построено по принципу **offline-first**: интерфейс читает и пишет
 * только сюда, а сеть — необязательный фоновой процесс. Отсюда следуют два
 * практических свойства: экраны открываются мгновенно (нет ожидания сети) и
 * приложение полностью работоспособно в метро и самолёте.
 *
 * ## Про производительность на больших объёмах
 *
 * Главный риск долгоживущего планировщика — деградация через два-три года,
 * когда накопятся десятки тысяч записей. Защита строится на трёх правилах:
 *
 * 1. **Составные индексы под реальные запросы.** Экран дня делает
 *    `where('[userId+plannedDate]').equals([uid, date])` — это точечный поиск
 *    по индексу, его стоимость не зависит от размера таблицы. Полные переборы
 *    (`toArray()` без `where`) в коде запрещены.
 *
 * 2. **Правила вместо материализации.** Режим сна и питания — это ~5 записей-
 *    правил, а не 5 × 365 записей в год. Конкретные дни вычисляются на лету.
 *
 * 3. **Точечная реактивность.** `useLiveQuery` перерисовывает компонент только
 *    при изменении данных, попавших в его запрос, а не при любой записи в БД.
 *
 * ## Как добавить поле или таблицу
 *
 * Увеличьте номер версии и опишите новый `stores()`. Dexie применит миграцию
 * при следующем открытии. Никогда не меняйте существующую версию — у
 * пользователей на устройствах уже лежат данные в старой схеме.
 */

import Dexie, { type EntityTable } from 'dexie';

import { canonicalGenres } from '@/shared/config/genres';
import { guessMediaFormat } from '@/shared/config/media-formats';

import type {
  AppSetting,
  BackupSnapshot,
  CalendarEvent,
  CustomSphere,
  DoseEvent,
  Goal,
  Habit,
  HabitEntry,
  HealthRecord,
  Medication,
  MediaItem,
  OutboxRecord,
  ProductPreference,
  Profile,
  Routine,
  RoutineOverride,
  ShoppingItem,
  ShoppingList,
  StreakState,
  Task,
  User,
} from './schema';
import { DEFAULT_HABIT_TARGET_DAYS } from './schema';

import { getCurrentUserId } from './session-context';

export class KairoDatabase extends Dexie {
  users!: EntityTable<User, 'id'>;
  profiles!: EntityTable<Profile, 'id'>;

  /** Свои сферы жизни: встроенные восемь в базе не лежат. */
  spheres!: EntityTable<CustomSphere, 'id'>;

  routines!: EntityTable<Routine, 'id'>;
  routineOverrides!: EntityTable<RoutineOverride, 'id'>;

  tasks!: EntityTable<Task, 'id'>;
  events!: EntityTable<CalendarEvent, 'id'>;

  goals!: EntityTable<Goal, 'id'>;
  habits!: EntityTable<Habit, 'id'>;
  habitEntries!: EntityTable<HabitEntry, 'id'>;

  healthRecords!: EntityTable<HealthRecord, 'id'>;
  medications!: EntityTable<Medication, 'id'>;
  doseEvents!: EntityTable<DoseEvent, 'id'>;

  shoppingLists!: EntityTable<ShoppingList, 'id'>;
  shoppingItems!: EntityTable<ShoppingItem, 'id'>;
  productPreferences!: EntityTable<ProductPreference, 'id'>;

  mediaItems!: EntityTable<MediaItem, 'id'>;

  /** Автоматические снимки данных: служебная таблица, наружу не выгружается. */
  backups!: EntityTable<BackupSnapshot, 'id'>;

  outbox!: EntityTable<OutboxRecord, 'id'>;
  settings!: EntityTable<AppSetting, 'key'>;
  streaks!: EntityTable<StreakState, 'id'>;

  constructor(name = 'kairo') {
    super(name);

    /**
     * Версия 1 — начальная схема.
     *
     * Первое поле — первичный ключ. Далее перечислены индексы; квадратные
     * скобки означают составной индекс. Индексируются только те поля, по
     * которым реально идёт поиск: каждый лишний индекс замедляет запись.
     */
    this.version(1).stores({
      users: 'id, &email',
      profiles: 'id, &userId, updatedAt',

      // Режим дня: правил мало, достаточно индекса по владельцу.
      routines: 'id, userId, [userId+kind], position',
      routineOverrides: 'id, userId, [userId+date], [routineId+date]',

      // Задачи — самая нагруженная таблица, индексы под все экраны:
      // день календаря, список по статусу, дедлайны, задачи цели.
      tasks:
        'id, userId, [userId+plannedDate], [userId+status], [userId+dueDate], ' +
        '[userId+status+plannedDate], goalId, habitId, updatedAt',

      events: 'id, userId, [userId+date], updatedAt',

      goals: 'id, userId, [userId+status], [userId+sphereId], position',
      habits: 'id, userId, [userId+archivedAt], goalId, position',
      // Уникальный составной ключ: одна отметка на привычку в день.
      habitEntries: 'id, userId, &[habitId+date], [userId+date], date',

      healthRecords: 'id, userId, &[userId+date], date',

      medications: 'id, userId, [userId+isActive], updatedAt',
      doseEvents: 'id, userId, [userId+date], [medicationId+date], [userId+status], date',

      shoppingLists: 'id, userId, [userId+plannedDate], position',
      shoppingItems: 'id, userId, listId, [listId+isChecked], position',
      productPreferences: 'id, userId, &[userId+productKey], usageCount',

      mediaItems: 'id, userId, [userId+kind], [userId+kind+status], updatedAt',

      // Очередь синхронизации разбирается в порядке добавления.
      outbox: 'id, userId, createdAt, entity, [entity+entityId]',
      settings: 'key',
      streaks: 'id, &userId',
    });

    /**
     * Версия 2 — повторяющиеся задачи.
     *
     * Добавлен индекс по `seriesId`: он нужен, чтобы найти все экземпляры
     * цепочки, когда пользователь меняет правило «для всей серии».
     *
     * `upgrade` проставляет значения новым полям у уже существующих задач.
     * Без этого шага старые записи пришли бы в код с `undefined`, и каждое
     * место чтения обязано было бы это учитывать.
     */
    this.version(2)
      .stores({
        tasks:
          'id, userId, [userId+plannedDate], [userId+status], [userId+dueDate], ' +
          '[userId+status+plannedDate], goalId, habitId, seriesId, updatedAt',
      })
      .upgrade((tx) =>
        tx
          .table('tasks')
          .toCollection()
          .modify((task: Record<string, unknown>) => {
            // Только недостающие поля. Безусловное присваивание затёрло бы
            // настоящие данные, если миграция когда-нибудь переиграется —
            // например, после отката версии или сбоя посередине.
            task.recurrence ??= 'none';
            task.recurrenceInterval ??= 1;
            task.recurrenceWeekdays ??= 0;
            task.recurrenceFrom ??= 'planned';
            task.recurrenceUntil ??= null;
            task.seriesId ??= null;
          }),
      );

    /**
     * Версия 3 — настраиваемая норма шагов.
     *
     * Индексы не меняются: поле только читается вместе с профилем. Миграция
     * нужна, чтобы у существующих профилей появилось значение — иначе код
     * получал бы `undefined` и в каждом месте пришлось бы это учитывать.
     */
    this.version(3).upgrade((tx) =>
      tx
        .table('profiles')
        .toCollection()
        .modify((profile: Record<string, unknown>) => {
          profile.stepsGoal ??= 10_000;
        }),
    );

    /**
     * Версия 4 — настраиваемый срок закрепления привычки.
     *
     * Индексы не меняются: поле участвует только в расчётах. Существующим
     * привычкам проставляется значение по умолчанию — иначе прогресс цели,
     * который теперь на него опирается, делился бы на `undefined`.
     */
    this.version(4).upgrade((tx) =>
      tx
        .table('habits')
        .toCollection()
        .modify((habit: Record<string, unknown>) => {
          habit.targetDays ??= DEFAULT_HABIT_TARGET_DAYS;
        }),
    );

    /**
     * Версия 5 — уборка индексов.
     *
     * Каждый индекс — налог на запись: сохраняя задачу, браузер обновляет их
     * все. На самой нагруженной таблице пять индексов не использовались ни
     * одним запросом, а три по базе были построены на логическом поле или на
     * `null`: такой ключ IndexedDB считает невалидным и запись в индекс просто
     * не кладёт — то есть индекс всегда пуст и существует только ради расходов.
     *
     * Добавлены три — под запросы, которые до сих пор обходились перебором:
     * архив выполненного, очередь синхронизации в порядке создания и поиск
     * записи каталога по внешнему идентификатору.
     *
     * Данные при смене индексов не переписываются: Dexie перестраивает сами
     * индексы, а `upgrade` здесь нужен ровно для одного поля.
     */
    this.version(5)
      .stores({
        tasks:
          'id, userId, [userId+plannedDate], [userId+status], ' +
          '[userId+status+plannedDate], [userId+status+completedAt], habitId, seriesId',

        events: 'id, userId, [userId+date]',
        goals: 'id, userId, [userId+status]',
        habits: 'id, userId, goalId',
        habitEntries: 'id, userId, &[habitId+date], [userId+date]',
        healthRecords: 'id, userId, &[userId+date]',

        medications: 'id, userId',
        doseEvents: 'id, userId, [userId+date], [medicationId+date]',

        shoppingLists: 'id, userId, [userId+plannedDate]',
        shoppingItems: 'id, userId, listId',
        productPreferences: 'id, userId, &[userId+productKey]',

        mediaItems:
          'id, userId, [userId+kind], [userId+kind+status], [userId+externalSource+externalId]',

        profiles: 'id, &userId',
        outbox: 'id, userId, [userId+createdAt], [entity+entityId]',
      })
      .upgrade((tx) =>
        // Архив выполненного теперь читается по индексу, где ключ — время
        // закрытия. Задача без него в индекс не попадёт и пропала бы с экрана
        // «Выполнено», поэтому недостающее берётся из последней правки: это
        // ближайшее к правде время, которое вообще известно.
        tx
          .table('tasks')
          .toCollection()
          .modify((task: Record<string, unknown>) => {
            if (task.status === 'done' && task.completedAt == null) {
              task.completedAt = task.updatedAt ?? Date.now();
            }
          }),
      );

    /**
     * Версия 6 — привязка курса к приёмам пищи.
     *
     * Раньше «после обеда» превращалось в число при нажатии: бралось время
     * обеда того дня и записывалось в расписание навсегда. Сдвинутый на один
     * день обед уезжал во все следующие дни.
     *
     * Теперь курс хранит, к каким правилам режима он привязан, а время
     * считается на каждый день. Существующим курсам список пустой: их время
     * уже проставлено числами, и трогать его нельзя — иначе после обновления
     * расписание разъехалось бы у тех, кто ничего не менял.
     */
    this.version(6).upgrade((tx) =>
      tx
        .table('medications')
        .toCollection()
        .modify((medication: Record<string, unknown>) => {
          medication.mealRoutineIds ??= [];
        }),
    );

    /**
     * Версия 7 — свои сферы жизни.
     *
     * Отдельная таблица, а не список в профиле: сфера — сущность, на которую
     * ссылаются задачи, цели и привычки, и синхронизироваться она должна как
     * все прочие, со своей ревизией и мягким удалением.
     *
     * Данные не переписываются: у существующих записей ссылки на встроенные
     * сферы, а встроенные в базе и не хранятся.
     */
    this.version(7).stores({ spheres: 'id, userId' });

    /**
     * Версия 8 — приём лекарства помнит, из какой еды посчитано его время.
     *
     * Индекс не нужен: связь читается вместе с самим приёмом, когда расписание
     * сверяется с базой. Существующие записи получают `null` — время у них
     * задано часами, и это правда для всех курсов, не привязанных к еде.
     * Привязанным связь проставится при первой же сверке расписания: пока еду
     * не двигали, время приёма и время еды совпадают.
     */
    this.version(8).upgrade((tx) =>
      tx
        .table('doseEvents')
        .toCollection()
        .modify((dose: Record<string, unknown>) => {
          dose.mealRoutineId ??= null;
        }),
    );

    /**
     * Версия 9 — автоматическая копия за день.
     *
     * Индекс по владельцу и дню: копия спрашивает «есть ли снимок за сегодня»,
     * и это должен быть точечный запрос, а не перебор.
     */
    this.version(9).stores({ backups: 'id, userId, [userId+date]' });

    /**
     * Версия 10 — снимки хранятся сжатыми.
     *
     * Копий теперь до двадцати двух, и в несжатом виде они вытеснили бы из
     * хранилища сами данные. Прежние снимки лежали строкой и новому формату не
     * соответствуют — они просто удаляются: снимок это слепок текущих данных,
     * и следующее открытие приложения сделает его заново.
     */
    this.version(10).upgrade((tx) => tx.table('backups').clear());

    /**
     * Версия 11 — встречи знают своё место в календаре устройства.
     *
     * Индекс не нужен: связь читается вместе со встречей при сверке, а сверка
     * и так берёт весь диапазон дней. Существующие встречи заведены здесь и
     * ни с чем не связаны — им проставляется `null`.
     */
    this.version(11).upgrade((tx) =>
      tx
        .table('events')
        .toCollection()
        .modify((event: Record<string, unknown>) => {
          event.externalId ??= null;
          event.externalCalendarId ??= null;
          event.syncedHash ??= null;
        }),
    );

    /**
     * Версия 12 — задачи тоже знают своё место в календаре устройства.
     *
     * Задача, поставленная на время, занимает его так же, как встреча, и
     * уезжает в общий календарь наравне с ней.
     *
     * Индекс по `externalId` нужен обеим таблицам ради одной защиты: перед тем
     * как завести запись по событию из календаря, сверка проверяет, нет ли у
     * этого события пары за пределами сверяемого окна. Без такой проверки
     * запись, передвинутая на полгода вперёд, вернулась бы из календаря
     * двойником — а задвоение данных для синхронизации худший исход.
     */
    this.version(12)
      .stores({
        tasks:
          'id, userId, [userId+plannedDate], [userId+status], ' +
          '[userId+status+plannedDate], [userId+status+completedAt], habitId, seriesId, externalId',

        events: 'id, userId, [userId+date], externalId',
      })
      .upgrade((tx) =>
        tx
          .table('tasks')
          .toCollection()
          .modify((task: Record<string, unknown>) => {
            task.externalId ??= null;
            task.externalCalendarId ??= null;
            task.syncedHash ??= null;
          }),
      );

    /**
     * Версия 13 — задачи знают своё место в «Напоминаниях».
     *
     * Связь отдельная от календарной: это два разных хранилища с разными
     * номерами записей, и общее поле однажды привело бы к тому, что сверка
     * календаря удалила бы напоминание, приняв его номер за номер события.
     *
     * Индекс по `reminderId` — ради той же защиты от двойников, что и у
     * календаря: перед тем как завести задачу по напоминанию, сверка
     * проверяет, нет ли у него пары.
     */
    this.version(13)
      .stores({
        tasks:
          'id, userId, [userId+plannedDate], [userId+status], ' +
          '[userId+status+plannedDate], [userId+status+completedAt], habitId, seriesId, ' +
          'externalId, reminderId',
      })
      .upgrade((tx) =>
        tx
          .table('tasks')
          .toCollection()
          .modify((task: Record<string, unknown>) => {
            task.reminderId ??= null;
            task.reminderListId ??= null;
            task.reminderHash ??= null;
          }),
      );

    /**
     * Версия 14 — две ступени приоритета вместо трёх.
     *
     * «Низкий» отличался от обычного только местом в списке: человек его
     * выбирал и не видел ровно ничего. Выбор без последствий учит не доверять
     * настройкам, поэтому ступеней стало две.
     *
     * Существующие «низкие» задачи становятся обычными. Порядок в списке от
     * этого меняется — но он и раньше не был человеку виден, а невидимую
     * настройку тихо сохранять ценой непонятного типа данных незачем.
     */
    this.version(14).upgrade((tx) =>
      tx
        .table('tasks')
        .toCollection()
        .modify((task: Record<string, unknown>) => {
          if (task.priority === 'low') task.priority = 'normal';
        }),
    );

    /**
     * Версия 15 — аптечка: срок годности, вскрытие, назначение и место.
     *
     * Поля добавляются пустыми, а не угадываются. Срок годности — не то, что
     * можно вывести из данных: он стоит на упаковке, и приложение, придумавшее
     * его за человека, однажды разрешит принять просроченное.
     */
    this.version(15).upgrade((tx) =>
      tx
        .table('medications')
        .toCollection()
        .modify((medication: Record<string, unknown>) => {
          medication.expiryDate ??= null;
          medication.openedAt ??= null;
          medication.openedShelfDays ??= null;
          medication.purposeKey ??= null;
          medication.storagePlace ??= '';
        }),
    );

    /**
     * Версия 16 — курс может состоять из нескольких лекарств.
     *
     * Мазь, которую смешивают из двух тюбиков, — это одно применение и две
     * упаковки. Раньше запись была одна на всё: переименование тюбика в
     * аптечке меняло название курса, а срок годности был общий на две разные
     * коробки. Существующие курсы остаются как есть — связать их за человека
     * нельзя, он сам знает, из чего состоит его мазь.
     */
    this.version(16).upgrade((tx) =>
      tx
        .table('medications')
        .toCollection()
        .modify((medication: Record<string, unknown>) => {
          medication.componentIds ??= [];
        }),
    );

    /**
     * Версия 17 — выключатель напоминаний становится настоящим.
     *
     * Поле было в схеме с самого начала и ни на что не влияло: напоминания
     * приходили, пока разрешение выдано системой, а выключить их из
     * приложения было нечем — только запретить уведомления насовсем в
     * настройках телефона. Теперь поле управляет рассылкой, и существующие
     * профили переводятся во «включено»: сегодня напоминания у них идут, и
     * обновление не должно их молча выключить.
     */
    this.version(17).upgrade((tx) =>
      tx
        .table('profiles')
        .toCollection()
        .modify((profile: Record<string, unknown>) => {
          profile.notificationsEnabled = true;
        }),
    );

    /**
     * Версия 18 — знакомство с приложением для тех, кто только пришёл.
     *
     * Существующим профилям оно помечается пройденным: человек, который ведёт
     * здесь дела не первый месяц, не должен после обновления упереться в
     * рассказ о том, где у приложения календарь. Пересмотреть знакомство он
     * может из профиля в любой момент.
     */
    this.version(18).upgrade((tx) =>
      tx
        .table('profiles')
        .toCollection()
        .modify((profile: Record<string, unknown>) => {
          profile.onboardingCompletedAt ??= Date.now();
        }),
    );

    /*
      Время закрытия не может быть позже последней правки: закрытие — это и
      есть правка. Позже оно оказывалось у задач, перенесённых из чужого файла
      в 0.4.19: время закрытия бралось из даты задачи, в том числе будущей. В
      архиве такие задачи вставали выше всего, что человек закрыл на самом
      деле, а при большом объёме вытесняли свежие из выборки целиком.

      Схема индексов при этом не меняется — меняются только значения.
    */
    this.version(19).upgrade((tx) =>
      tx
        .table('tasks')
        .toCollection()
        .modify((task: Record<string, unknown>) => {
          const completedAt = task.completedAt;
          const updatedAt = task.updatedAt;
          if (
            task.status === 'done' &&
            typeof completedAt === 'number' &&
            typeof updatedAt === 'number' &&
            completedAt > updatedAt
          ) {
            task.completedAt = updatedAt;
          }
        }),
    );

    /**
     * Версия 20 — формат фильма: фильм, сериал, мультфильм.
     *
     * Лежавшие в базе фильмы получают его по жанрам (`guessMediaFormat`):
     * других сведений о них нет. Книгам формат не нужен: `null`.
     */
    this.version(20).upgrade((tx) =>
      tx
        .table('mediaItems')
        .toCollection()
        .modify((item: Record<string, unknown>) => {
          if (item.format !== undefined) return;
          item.format =
            item.kind === 'movie'
              ? guessMediaFormat(Array.isArray(item.genres) ? (item.genres as string[]) : [])
              : null;
        }),
    );

    /** Версия 21 — отметка «Любимое» у книг и фильмов. До неё любимого не было. */
    this.version(21).upgrade((tx) =>
      tx
        .table('mediaItems')
        .toCollection()
        .modify((item: Record<string, unknown>) => {
          item.favorite ??= false;
        }),
    );

    /**
     * Версия 22 — ссылка на статью Википедии, из которой взято описание. До неё
     * описания были только из каталогов.
     */
    this.version(22).upgrade((tx) =>
      tx
        .table('mediaItems')
        .toCollection()
        .modify((item: Record<string, unknown>) => {
          item.descriptionUrl ??= null;
        }),
    );

    /**
     * Версия 23 — у записей из каталога только основные жанры
     * (`canonicalGenres`): «фильм-ограбление», «сериал-антология», «Музыка»
     * жанрами не считаются. То, что человек вписал сам, не трогаем.
     */
    this.version(23).upgrade((tx) =>
      tx
        .table('mediaItems')
        .toCollection()
        .modify((item: Record<string, unknown>) => {
          if (item.externalSource === 'manual' || !Array.isArray(item.genres)) return;
          item.genres = canonicalGenres(item.genres as string[]);
        }),
    );

    /**
     * Версия 24 — магазин у позиции списка покупок и у товара в личном
     * справочнике: там он запоминается и подставляется. До неё не указывался.
     */
    this.version(24).upgrade(async (tx) => {
      for (const table of ['shoppingItems', 'productPreferences']) {
        await tx
          .table(table)
          .toCollection()
          .modify((row: Record<string, unknown>) => {
            row.store ??= '';
          });
      }
    });

    /*
      Прежняя сверка календаря, удаляя встречу здесь вслед за телефоном,
      оставляла ей связь с событием. Стоило событию вернуться — календарь снова
      отметили, встречу восстановили из корзины, — и эта связь стирала его уже
      в телефоне. Удалённой записи связь не нужна: она снимается со всех.
    */
    this.version(25).upgrade(async (tx) => {
      for (const table of ['events', 'tasks']) {
        await tx
          .table(table)
          .toCollection()
          .modify((row: Record<string, unknown>) => {
            if (row.deletedAt == null || row.externalId == null) return;
            row.externalId = null;
            row.syncedHash = null;
          });
      }
    });
  }

  /**
   * Полная очистка данных пользователя — «выйти и забыть всё на устройстве».
   * Выполняется одной транзакцией: либо очищается всё, либо ничего.
   */
  async wipeUserData(userId: string): Promise<void> {
    // Все таблицы, кроме `users` (учётные записи) и `settings` (настройки
    // устройства): они не привязаны к конкретному аккаунту.
    const tables = this.tables.filter(
      (table) => table.name !== 'users' && table.name !== 'settings',
    );

    await this.transaction('rw', tables, async () => {
      await Promise.all(tables.map((table) => table.where('userId').equals(userId).delete()));
    });
  }
}

/** Единственный экземпляр БД на всё приложение. */
export const db = new KairoDatabase();

/**
 * Диагностика: сколько записей в каждой таблице.
 * Используется в разделе «Данные» настроек — помогает и пользователю,
 * и разработчику при отладке проблем с производительностью.
 */
export async function getDatabaseStats(): Promise<Record<string, number>> {
  const userId = getCurrentUserId();
  if (!userId) return {};

  /**
   * Считаются только живые записи владельца.
   *
   * Раньше считались все строки всех таблиц: и чужие аккаунты устройства, и
   * мягко удалённое, и служебная очередь обмена. Из-за этого итог «Записей в
   * базе» не сходился с суммой строк под ним — 241 против 120, — и число
   * молча раскрывало объём чужих данных.
   */
  const entries = await Promise.all(
    db.tables.map(async (table) => {
      // Снимки — это копии тех же записей, а не отдельные данные: попади они в
      // счётчик, «Записей в базе» удвоилось бы за одну ночь.
      if (table.name === 'backups') return [table.name, 0] as const;

      if (!table.schema.indexes.some((index) => index.name === 'userId'))
        return [table.name, 0] as const;

      const rows = (await table.where('userId').equals(userId).toArray()) as {
        deletedAt: number | null;
      }[];

      return [table.name, rows.filter((row) => row.deletedAt === null).length] as const;
    }),
  );

  return Object.fromEntries(entries.filter(([, count]) => count > 0));
}
